import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { UserCircle, LogIn, UserPlus, Sparkles } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

export default function PatientPortalLanding() {
  const { slug } = useParams();
  const location = useLocation();
  const isJoinMode = /^\/(patient|paciente|paziente)\//.test(location.pathname);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [mode, setMode] = useState("register"); // register | login
  const [step, setStep] = useState(1);          // 1 = account, 2 = anamnesi (register only)
  const [form, setForm] = useState({
    email: "", password: "", full_name: "",
    phone: "", date_of_birth: "", codice_fiscale: "",
    allergies: "", diseases: "", medications: "", notes: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If already logged in, skip landing
    if (localStorage.getItem("patient_token")) { navigate("/mi-portal"); return; }
    if (slug) {
      api.get(`/public/dr/${slug}`)
        .then((r) => setDoctor(r.data))
        .catch(() => toast.error(t("landing.professionalNotFound") || "Profesional no encontrado"));
    } else {
      // Generic /portal — set doctor to Ambra placeholder so UI renders
      setDoctor({ name: "Ambra", clinic_name: "", specialization: "" });
      setMode("login"); // no slug ⇒ always login-only path
    }
  }, [slug, navigate, t]);

  async function submit(e) {
    e.preventDefault();
    if (mode === "register" && step === 1) { setStep(2); return; }
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/patient-auth/register" : "/patient-auth/login";
      const payload = mode === "register"
        ? { ...form, doctor_slug: slug }
        : { email: form.email, password: form.password, doctor_slug: slug };
      const { data } = await api.post(endpoint, payload);
      localStorage.setItem("patient_token", data.token);
      localStorage.setItem("patient_data", JSON.stringify(data.patient));
      toast.success(mode === "register" ? t("landing.welcome") : t("landing.loggedIn"));
      navigate("/mi-portal");
    } catch (e2) {
      const msg = apiError(e2);
      toast.error(msg);
      if (mode === "register" && /cuenta en Ambra|già una registrazione|Inicia sesión/i.test(msg)) {
        setMode("login"); setStep(1);
      }
    }
    finally { setLoading(false); }
  }

  if (!doctor) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center text-stone-500">Caricamento…</div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="patient-portal-landing">
      <header className="border-b border-stone-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-heading text-2xl font-light">Ambra</span>
            <span className="text-xs uppercase tracking-[0.25em] text-stone-400">patient matter</span>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-5xl w-full grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B07D4F]">
              {isJoinMode ? t("landing.joinEyebrow") : t("landing.eyebrow")}
            </p>
            <h1 className="font-heading text-5xl lg:text-6xl font-light tracking-tight">
              {t("landing.hello")},<br />{t("landing.imDr")} <span className="text-[#B07D4F]">{doctor.name}</span>.
            </h1>
            {doctor.specialization && <p className="text-lg text-stone-600">{doctor.specialization}</p>}
            {doctor.clinic_name && <p className="text-sm text-stone-500 uppercase tracking-widest">{doctor.clinic_name}</p>}
            <div className="border-l-2 border-[#D4A373] pl-6 mt-8 space-y-3 text-stone-600">
              {isJoinMode ? (
                <>
                  <div className="flex items-start gap-3"><Sparkles className="w-4 h-4 mt-1 text-[#D4A373]" strokeWidth={1.5} /><p>{t("landing.joinB2")}</p></div>
                  <div className="flex items-start gap-3"><Sparkles className="w-4 h-4 mt-1 text-[#D4A373]" strokeWidth={1.5} /><p>{t("landing.joinB3")}</p></div>
                  <div className="flex items-start gap-3"><Sparkles className="w-4 h-4 mt-1 text-[#D4A373]" strokeWidth={1.5} /><p><strong>{t("landing.free")}</strong></p></div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3"><Sparkles className="w-4 h-4 mt-1 text-[#D4A373]" strokeWidth={1.5} /><p>{t("landing.b1")}</p></div>
                  <div className="flex items-start gap-3"><Sparkles className="w-4 h-4 mt-1 text-[#D4A373]" strokeWidth={1.5} /><p>{t("landing.b2")}</p></div>
                  <div className="flex items-start gap-3"><Sparkles className="w-4 h-4 mt-1 text-[#D4A373]" strokeWidth={1.5} /><p>{t("landing.b3")}</p></div>
                  <div className="flex items-start gap-3"><Sparkles className="w-4 h-4 mt-1 text-[#D4A373]" strokeWidth={1.5} /><p><strong>{t("landing.free")}</strong></p></div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm">
            <div className="flex gap-2 mb-6 p-1 bg-[#FAF9F6] rounded-full">
              <button data-testid="tab-register" onClick={() => { setMode("register"); setStep(1); }}
                className={`flex-1 py-2 rounded-full text-sm uppercase tracking-widest transition-colors ${mode === "register" ? "bg-stone-900 text-white" : "text-stone-500"}`}>
                <UserPlus className="w-3.5 h-3.5 inline mr-1.5" /> {t("landing.create")}
              </button>
              <button data-testid="tab-login" onClick={() => { setMode("login"); setStep(1); }}
                className={`flex-1 py-2 rounded-full text-sm uppercase tracking-widest transition-colors ${mode === "login" ? "bg-stone-900 text-white" : "text-stone-500"}`}>
                <LogIn className="w-3.5 h-3.5 inline mr-1.5" /> {t("landing.enter")}
              </button>
            </div>

            {mode === "register" && (
              <p className="text-xs text-stone-500 mb-4 uppercase tracking-widest">{t("landing.step")} {step} / 2</p>
            )}

            <form onSubmit={submit} className="space-y-4">
              {(mode === "login" || step === 1) && (
                <>
                  {mode === "register" && (
                    <Field label={t("landing.fullName")} testId="input-fullname" required
                      value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
                  )}
                  <Field label="Email" type="email" required testId="input-email"
                    value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                  <Field label="Password" type="password" required testId="input-password" minLength={6}
                    value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
                </>
              )}

              {mode === "register" && step === 2 && (
                <>
                  <p className="text-xs text-stone-500 mb-2">{t("landing.step2Desc")}</p>
                  <Field label={t("booking.phone")} required testId="input-phone" placeholder="+39 …"
                    value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("booking.dob")} type="date" testId="input-dob"
                      value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} />
                    <Field label={t("booking.cf")} testId="input-cf"
                      value={form.codice_fiscale} onChange={(v) => setForm({ ...form, codice_fiscale: v })} />
                  </div>
                  <TextArea label={t("booking.allergies")} rows={2} testId="input-allergies"
                    value={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} />
                  <TextArea label={t("booking.diseases")} rows={2} testId="input-diseases"
                    value={form.diseases} onChange={(v) => setForm({ ...form, diseases: v })} />
                  <TextArea label={t("booking.medications")} rows={2} testId="input-medications"
                    value={form.medications} onChange={(v) => setForm({ ...form, medications: v })} />
                </>
              )}

              <div className="flex gap-2">
                {mode === "register" && step === 2 && (
                  <button type="button" onClick={() => setStep(1)}
                    className="px-4 py-3 rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50 text-sm">
                    ← {t("common.back")}
                  </button>
                )}
                <button type="submit" disabled={loading} data-testid="submit-btn"
                  className="flex-1 py-3 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors disabled:opacity-50 font-medium">
                  {loading ? "…" : (mode === "register"
                    ? (step === 1 ? t("landing.next") : t("landing.createAccount"))
                    : t("landing.enterBtn"))}
                </button>
              </div>
            </form>
            <p className="text-xs text-stone-400 mt-6 text-center">
              <UserCircle className="w-3 h-3 inline" /> {t("landing.gdpr", { name: doctor.name })}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testId, minLength }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1.5 block">{label}</label>
      <input type={type} value={value} required={required} placeholder={placeholder} minLength={minLength}
        data-testid={testId} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B]" />
    </div>
  );
}

function TextArea({ label, value, onChange, rows, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1.5 block">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
        spellCheck autoCorrect="on" autoCapitalize="sentences" data-testid={testId}
        className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B]" />
    </div>
  );
}
